/**
 * Entity geometry helpers for the CAD modify commands (move/copy/rotate/
 * mirror/scale). All drafting payloads are treated generically through a
 * small registry of point-bearing keys in the entity `data` bag — so every
 * entity type (line, circle, arc, ellipse, polyline, text, box, ...) is
 * transformed uniformly. World frame is Z-up (core/scene/axes): planar
 * transforms operate on (x, y) and leave the level coordinate untouched.
 */

import * as THREE from 'three';
import type { Entity } from '../../state/store';
import { useAppStore } from '../../state/store';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { transformSceneObjects } from '../../core/scene/editRouter';

/** Scalar keys that hold simple 3D points [x, y, z]. */
const POINT_KEYS = ['position', 'center', 'point', 'corner1', 'corner2', 'radiusPoint', 'textPosition', 'startPoint', 'endPoint', 'axisStart', 'axisEnd', 'basePoint'];
/** Keys holding polylines of points. */
const PATH_KEYS = ['vertices', 'controlPoints', 'boundary', 'path', 'points'];
/** Scalar keys that scale linearly. */
const SCALAR_KEYS = ['radius', 'radiusTop', 'radiusBottom', 'height', 'width', 'thickness', 'depth'];

type DataBag = Record<string, any>;

/** Deep-ish clone of an entity data bag (points/arrays copied). */
export function cloneEntityData(data: DataBag): DataBag {
    const out: DataBag = {};
    for (const [k, v] of Object.entries(data ?? {})) {
        if (Array.isArray(v)) {
            out[k] = v.map((x) => (Array.isArray(x) ? [...x] : x));
        } else {
            out[k] = v;
        }
    }
    return out;
}

/** Apply an in-place point transform to every point-bearing field. */
function transformPoints(data: DataBag, fn: (p: number[]) => number[], scaleScalars = 0): DataBag {
    const next = cloneEntityData(data);
    for (const key of POINT_KEYS) {
        const v = next[key];
        if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number') {
            next[key] = fn(v);
        }
    }
    for (const key of PATH_KEYS) {
        const v = next[key];
        if (Array.isArray(v)) {
            next[key] = v.map((p) => (Array.isArray(p) ? fn(p) : p));
        }
    }
    if (scaleScalars > 0) {
        for (const key of SCALAR_KEYS) {
            if (typeof next[key] === 'number') next[key] *= scaleScalars;
        }
        if (Array.isArray(next.size)) {
            next.size = (next.size as number[]).map((s) => s * scaleScalars);
        }
    }
    return next;
}

/** Translate every point by a delta (z included). */
export function translateData(data: DataBag, delta: [number, number, number]): DataBag {
    return transformPoints(data, (p) => [p[0] + delta[0], p[1] + delta[1], (p[2] ?? 0) + delta[2]]);
}

/**
 * After a point transform, carry the angular payload fields along:
 * arc spans (startAngle/endAngle) and the text/ellipse `rotation` must
 * rotate WITH the geometry — otherwise ROTATE leaves arcs at their old
 * opening and text upright (docs/plans/2d plan A4).
 */
function rotateAngularFields(data: DataBag, angleRad: number): DataBag {
    if (typeof data.rotation === 'number') data.rotation += angleRad;
    if (typeof data.startAngle === 'number' && typeof data.endAngle === 'number') {
        data.startAngle += angleRad;
        data.endAngle += angleRad;
    }
    return data;
}

/** Rotate around a center in the XY plane (Z level preserved). */
export function rotateData(data: DataBag, center: [number, number], angleRad: number): DataBag {
    const c = new THREE.Vector2(center[0], center[1]);
    const next = transformPoints(data, (p) => {
        const v = new THREE.Vector2(p[0], p[1]).sub(c).rotateAround(new THREE.Vector2(0, 0), angleRad).add(c);
        return [v.x, v.y, p[2] ?? 0];
    });
    return rotateAngularFields(next, angleRad);
}

/** Uniform scale about a base point (XY + scalars). */
export function scaleData(data: DataBag, base: [number, number], factor: number): DataBag {
    const b = new THREE.Vector2(base[0], base[1]);
    return transformPoints(
        data,
        (p) => {
            const v = new THREE.Vector2(p[0], p[1]).sub(b).multiplyScalar(factor).add(b);
            return [v.x, v.y, p[2] ?? 0];
        },
        factor
    );
}

/** Mirror across the infinite line through a-b in the XY plane. */
export function mirrorData(data: DataBag, a: [number, number], b: [number, number]): DataBag {
    const origin = new THREE.Vector2(a[0], a[1]);
    const d = new THREE.Vector2(b[0] - a[0], b[1] - a[1]);
    if (d.lengthSq() < 1e-12) return cloneEntityData(data);
    d.normalize();
    const next = transformPoints(data, (p) => {
        const pt = new THREE.Vector2(p[0], p[1]).sub(origin);
        const along = pt.dot(d);
        const footX = origin.x + d.x * along;
        const footY = origin.y + d.y * along;
        return [2 * footX - p[0], 2 * footY - p[1], p[2] ?? 0];
    });
    // Angular payload mirrors with the geometry: an arc at angle φ maps to
    // 2θ−φ (θ = mirror line angle) and its span direction flips (start/end
    // swap). Text rotation mirrors too, but MIRRTEXT=0 keeps it readable.
    const theta = Math.atan2(d.y, d.x);
    const mirrorAngle = (v: number) => 2 * theta - v;
    if (typeof next.rotation === 'number') {
        if ((useSystemVarsStore.getState().MIRRTEXT ?? 1) !== 0) next.rotation = mirrorAngle(next.rotation);
    }
    if (typeof next.startAngle === 'number' && typeof next.endAngle === 'number') {
        const s = mirrorAngle(next.endAngle);
        const e = mirrorAngle(next.startAngle);
        next.startAngle = s;
        next.endAngle = e;
    }
    return next;
}

/** Snapshot of current data for the given ids (for undo/redo closures). */
export function snapshotEntities(ids: string[]): Record<string, DataBag> {
    const { entities } = useAppStore.getState();
    const out: Record<string, DataBag> = {};
    ids.forEach((id) => {
        const e = entities[id];
        if (e) out[id] = cloneEntityData(e.data);
    });
    return out;
}

/**
 * Apply a transform to selected entities as ONE undoable history step.
 * Used by move/rotate/scale/mirror (in-place ops). Copy uses addEntity +
 * its own deletion-undo closure in ModifyTools.
 *
 * B4 cross-workbench contract: ids that resolve to another workbench's
 * citizen (BIM element, sketch solid/edge) are routed through the edit
 * router with the matrix equivalent of the same operation, so their owning
 * store (and undo chain) performs the edit. `foreign` carries that matrix;
 * when omitted, the command stays CAD-only.
 */
export interface ForeignTransformSpec {
    matrix: THREE.Matrix4;
    label: string;
}

export function commitEntityEdit(ids: string[], transform: (data: DataBag) => DataBag, foreign?: ForeignTransformSpec): void {
    if (ids.length === 0) return;
    const app = useAppStore.getState();
    const cadIds = foreign
        ? ids.filter((id) => !!app.entities[id])
        : ids;
    if (foreign) {
        const foreignIds = ids.filter((id) => !app.entities[id]);
        if (foreignIds.length > 0) {
            transformSceneObjects(foreignIds, foreign.matrix, foreign.label);
        }
    }
    if (cadIds.length === 0) return;
    const before = snapshotEntities(cadIds);
    const after: Record<string, DataBag> = {};
    cadIds.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        const next = transform(e.data);
        after[id] = cloneEntityData(next);
        app.updateEntity(id, { data: next });
    });
    app.pushHistory({
        type: 'modify',
        timestamp: Date.now(),
        data: { ids: cadIds },
        undo: () => {
            const a = useAppStore.getState();
            cadIds.forEach((id) => a.updateEntity(id, { data: before[id] ?? {} }));
        },
        redo: () => {
            const a = useAppStore.getState();
            cadIds.forEach((id) => a.updateEntity(id, { data: after[id] ?? {} }));
        },
    });
}

// ------------------------------------------------- matrix equivalents (B4)
// The data-bag transforms above express drafting ops on heterogeneous bags;
// cross-workbench citizens take the same op as one rigid matrix instead.

/** Translate matrix for the MOVE equivalent. */
export function translateMatrix(delta: [number, number, number]): THREE.Matrix4 {
    return new THREE.Matrix4().makeTranslation(delta[0], delta[1], delta[2]);
}

/** Rotation about the Z axis through `center` (ROTATE equivalent). */
export function rotateZMatrix(center: [number, number], angleRad: number): THREE.Matrix4 {
    return new THREE.Matrix4()
        .makeTranslation(center[0], center[1], 0)
        .multiply(new THREE.Matrix4().makeRotationZ(angleRad))
        .multiply(new THREE.Matrix4().makeTranslation(-center[0], -center[1], 0));
}

/** Uniform XY scale about `base`, level Z preserved (SCALE equivalent). */
export function scaleMatrix(base: [number, number], factor: number): THREE.Matrix4 {
    return new THREE.Matrix4()
        .makeTranslation(base[0], base[1], 0)
        .multiply(new THREE.Matrix4().makeScale(factor, factor, 1))
        .multiply(new THREE.Matrix4().makeTranslation(-base[0], -base[1], 0));
}

/** Reflection across the infinite XY line a→b, Z unchanged (MIRROR
 *  equivalent). Angular-field nuances (MIRRTEXT, arc span flip) are
 *  CAD-bag specifics and intentionally do not apply to foreign citizens. */
export function mirrorMatrix(a: [number, number], b: [number, number]): THREE.Matrix4 {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-12) return new THREE.Matrix4();
    const nx = dx / Math.sqrt(lenSq);
    const ny = dy / Math.sqrt(lenSq);
    // 2D reflection about direction n: M = 2·n·nᵀ − I (z row identity).
    const m00 = 2 * nx * nx - 1;
    const m01 = 2 * nx * ny;
    const m10 = 2 * nx * ny;
    const m11 = 2 * ny * ny - 1;
    // p ↦ M·p + (a − M·a): reflection about the line THROUGH a.
    const tx = a[0] - (m00 * a[0] + m01 * a[1]);
    const ty = a[1] - (m10 * a[0] + m11 * a[1]);
    return new THREE.Matrix4().set(
        m00, m01, 0, tx,
        m10, m11, 0, ty,
        0, 0, 1, 0,
        0, 0, 0, 1,
    );
}
